import { Box, Grid, OutlinedInputProps, TextField, Typography } from '@mui/material';
import classNames from 'classnames';
import React, { InputHTMLAttributes, useEffect } from 'react';
import { CountryData } from 'react-phone-input-2';
import PhoneField from '../../../components/FormInputs/PhoneField/PhoneField';
import useWidth from '../../../components/Hooks/useWidth';
import LangEnum from '../../../lang/LangEnum';
import useTeceoStyles from '../../../useTeceoStyles';
import { useApplicationContext } from '../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../translation/TranslationContext';
import { useSignatureContext } from './contexts/signature.context';
import { getModalContentXPadding } from './utils';

const ContractConfirmPersonalInfo = () => {
  const { translate } = useTranslation();
  const { state } = useApplicationContext();
  const { classes: teceoClasses } = useTeceoStyles();
  const { smallWidth } = useWidth();
  const px = getModalContentXPadding(smallWidth);

  const [values, setValues] = React.useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '',
  });

  const { changePersonalInfo, signingAs, personalInfo } = useSignatureContext();

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValues({ ...values, name: event.target.value });
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValues({ ...values, email: event.target.value });
  };

  const handlePhoneAndCountryCodeChage = (value: string, data: CountryData) => {
    setValues({ ...values, phone: value, countryCode: data.dialCode });
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (values.name && values.email && values.phone && values.countryCode) {
        changePersonalInfo(values);
      } else {
        changePersonalInfo(null);
      }
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [values, changePersonalInfo]);

  useEffect(() => {
    if (personalInfo) {
      setValues(personalInfo);
      return;
    }

    if (signingAs === 'BRAND') {
      const valuesFromState = {
        name: state?.user?.name || '',
        email: state?.user?.email || '',
        phone: state.user?.phone ? `${state.user?.countryCode || ''}${state.user.phone}` : '',
        countryCode: state?.user?.countryCode || '',
      };

      setValues(valuesFromState);
      changePersonalInfo(valuesFromState);

      return;
    }

    setValues({
      ...values,
      phone: state.user?.countryCode || '',
    });
  }, []);

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
      }}
    >
      <Box
        sx={{
          pt: 3,
          px: px,
          pb: 2,
        }}
      >
        <Typography
          variant="h4"
          color="textSecondary"
          className={classNames(teceoClasses.secondaryTypography, teceoClasses.upperTitle)}
          style={{
            fontSize: '14px',
          }}
        >
          {translate(LangEnum.ORDER_CONTRACT_CONFIRM_PERSONAL_INFO_FORM_TITLE)}
        </Typography>
      </Box>
      <Box
        sx={{
          px: px,
        }}
      >
        <Grid container spacing={3}>
          <Grid size={12}>
            <TextField
              variant="standard"
              value={values.name}
              size="small"
              fullWidth
              onChange={handleNameChange}
              label={translate(LangEnum.ORDER_CONTRACT_CONFIRM_PERSONAL_INFO_FORM_NAME)}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <Box
              sx={{
                pr: smallWidth ? 0 : 0.5,
              }}
            >
              <TextField
                variant="standard"
                value={values.email}
                onChange={handleEmailChange}
                size="small"
                fullWidth
                label={translate(LangEnum.ORDER_CONTRACT_CONFIRM_PERSONAL_INFO_FORM_EMAIL)}
              />
            </Box>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <Box
              sx={{
                pl: smallWidth ? 0 : 0.5,
              }}
            >
              <TextField
                variant="standard"
                fullWidth
                value={values.phone}
                size="small"
                label={translate(LangEnum.ORDER_CONTRACT_CONFIRM_PERSONAL_INFO_FORM_PHONE)}
                slotProps={{
                  // TODO: mesmo tratamento de CustomerContactsField:159 (tipos incompatíveis)
                  input: {
                    inputComponent: PhoneField as unknown as OutlinedInputProps['inputComponent'],
                    inputProps: {
                      onChange: (value: string, data: CountryData) => {
                        handlePhoneAndCountryCodeChage(value, data);
                      },
                    } as unknown as InputHTMLAttributes<HTMLInputElement>,
                  },
                  inputLabel: { shrink: true },
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ContractConfirmPersonalInfo;
